import { useEffect, useRef, useImperativeHandle, useState } from 'react';
import { useSelector } from 'react-redux';
import { useGroupKey, useJoinKeyPathToString, useNoOutlineWhenFocused, useRegisterInlineFieldEditor, useWasUndoRedoEventWithData, useInlineFieldEditingHoverStyles } from '../lib/InlineFieldEditingHooks';
import { setCaretPosition } from '../lib/domUtils/selectionDomUtils';
// @ts-expect-error not typed
import EditorConfigSingleton from 'ContentEditorUI/EditorConfigSingleton';
import { getCanUseFieldSettingsToolbar } from 'ContentEditorUI/redux/selectors/authSelectors';
import FieldSettingsToolbar from './fieldSettingsToolbar';
import { jsx as _jsx } from "react/jsx-runtime";
const TextFieldEditor = ({
  name,
  node,
  onUpdate,
  currentValue,
  keyPath,
  label,
  helpText,
  isRequired = false,
  requiredForSaving = false,
  supportsInlinePlaceholdersAndLabels,
  moduleName,
  onFocusCallback,
  onBlurCallback,
  registerInlineFieldRef,
  allowNewLine = true,
  moduleSchema,
  fieldOverrides
}) => {
  const textRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const isInteractingWithToolbarRef = useRef(false);
  const canUseFieldSettingsToolbar = useSelector(getCanUseFieldSettingsToolbar);
  useInlineFieldEditingHoverStyles(node);
  useNoOutlineWhenFocused(node);
  useRegisterInlineFieldEditor(moduleName);
  const fieldKeyString = useJoinKeyPathToString(keyPath);
  const groupKey = useGroupKey(moduleName, fieldKeyString, name);
  const wasUndoRedo = useWasUndoRedoEventWithData(currentValue);
  useImperativeHandle(textRef, () => {
    return {
      focus: () => {
        if (document.activeElement !== node) {
          node.focus();
          // We don't have click coordinates from the inline toolbar or sidebar
          // so default to manually clicking the top right of the field
          const {
            right,
            top
          } = node.getBoundingClientRect();
          setCaretPosition(right - 5, top + 5);
          onFocusCallback();
        }
      }
    };
  }, [node, onFocusCallback]);

  // @TODO: This may fall apart once we have modules with inline & non-inline fields
  // that experience a live update via Remy (because the ref depends on the module DOM node)
  useEffect(() => {
    registerInlineFieldRef === null || registerInlineFieldRef === void 0 || registerInlineFieldRef(name, 'text', textRef.current);
  }, [registerInlineFieldRef, name]);
  useEffect(() => {
    if (!supportsInlinePlaceholdersAndLabels) {
      return;
    }
    const inpageConfig = EditorConfigSingleton.get('inpage');
    if (inpageConfig && inpageConfig.shouldUseInlinePlaceholdersAndLabels) {
      // Setting data attributes so our css can render psuedo placeholders with the text on
      // the attributes when the field is empty
      if (helpText) {
        node.setAttribute('data-hs-inline-help-text', helpText);
      }
      if (label) {
        node.setAttribute('data-hs-inline-label', label);
        if (isRequired) {
          // Note: setting a value of '*' instead of `true` because the pseudo elements render the raw string
          // that lives on the data attribute
          node.setAttribute('data-hs-inline-label-required', '*');
        }
      }
    }
  }, [helpText, isRequired, label, node, supportsInlinePlaceholdersAndLabels]);
  // Set content editble on provided node + change listeners on them
  useEffect(() => {
    let textBeforeEditing = null;
    const updateListener = e => {
      const target = e.target;
      const updatedText = target.innerText;
      if (requiredForSaving && updatedText === '') {
        return;
      }
      onUpdate(keyPath, updatedText, {
        groupKey
      });
    };
    const onClick = e => {
      if (document.activeElement !== node) {
        textBeforeEditing = node.innerText;
        // If the field is required for saving, check to see if the current value
        // is equal to the translated help text. If it is, assume that the value is
        // placeholder content and clear it from the DOM on node focus.
        if (requiredForSaving && helpText && textBeforeEditing === helpText) {
          node.innerText = '';
        }
        node.focus();
      }
      setCaretPosition(e.clientX, e.clientY);
    };
    const onFocus = () => {
      setIsFocused(true);
      onFocusCallback();
    };
    const onBlur = () => {
      if (isInteractingWithToolbarRef.current) {
        return;
      }
      // If the field is required for saving and they're attempting to save an empty
      // string, replace the empty string with the content they had prior to making
      // any updates and save that old value again
      if (requiredForSaving && node.innerText === '' && textBeforeEditing) {
        node.innerText = textBeforeEditing;
        onUpdate(keyPath, textBeforeEditing, {
          groupKey
        });
      }
      textBeforeEditing = null;
      setIsFocused(false);
      onBlurCallback();
    };
    const onPaste = e => {
      if (e.clipboardData) {
        e.preventDefault();
        const paste = e.clipboardData.getData('text/plain');
        const selection = window.getSelection();
        if (!selection || !selection.rangeCount) {
          return;
        }
        selection.deleteFromDocument();
        selection.getRangeAt(0).insertNode(document.createTextNode(paste));
        selection.collapseToEnd();
        node.normalize();
        onUpdate(keyPath, node.innerText, {
          groupKey
        });
      }
    };
    const onKeyDown = e => {
      // Ignore events during IME composition (e.g., Japanese, Chinese, Korean input)
      if (e.isComposing) {
        return;
      }

      // If allow_new_line is false, prevent Enter key
      if (!allowNewLine && e.key === 'Enter') {
        e.preventDefault();
        node.blur(); // Commit changes by triggering blur
        return;
      }
    };
    node.setAttribute('contenteditable', 'true');
    node.addEventListener('input', updateListener);
    node.addEventListener('click', onClick);
    node.addEventListener('focus', onFocus);
    node.addEventListener('paste', onPaste);
    node.addEventListener('keydown', onKeyDown);
    node.addEventListener('blur', onBlur);
    return () => {
      node.removeEventListener('input', updateListener);
      node.removeEventListener('click', onClick);
      node.removeEventListener('focus', onFocus);
      node.removeEventListener('paste', onPaste);
      node.removeEventListener('keydown', onKeyDown);
      node.removeEventListener('blur', onBlur);
      node.removeAttribute('contenteditable');
    };
  }, [name, node, onUpdate, keyPath, onFocusCallback, onBlurCallback, groupKey, requiredForSaving, helpText, allowNewLine]);

  // Make sure updates from the sidebar are also applied to the DOM
  useEffect(() => {
    if (currentValue !== undefined && node.innerText !== currentValue && (
    // Always apply the `currentValue` if the change came from an undo/redo action
    document.activeElement !== node || wasUndoRedo)) {
      node.innerText = currentValue;
    }
  }, [currentValue, node, wasUndoRedo]);
  if (!isFocused || !moduleSchema || !canUseFieldSettingsToolbar) {
    return null;
  }
  return /*#__PURE__*/_jsx(FieldSettingsToolbar, {
    focusedField: {
      name,
      domNode: node,
      path: keyPath
    },
    moduleSchema: moduleSchema,
    fieldOverrides: fieldOverrides || {},
    onFieldChange: (path, value, undoData, metaData) => {
      if (fieldOverrides) {
        onUpdate(path, value, Object.assign({}, undoData, {
          groupKey
        }), metaData);
      }
    },
    onToolbarInteractionStart: () => {
      isInteractingWithToolbarRef.current = true;
    },
    onToolbarInteractionEnd: () => {
      isInteractingWithToolbarRef.current = false;
    }
    // Called when user clicks outside both the field and toolbar.
    // Native onBlur may not fire in this case since the click was outside
    // the contenteditable, so we explicitly clear focus state here.
    ,
    onClose: () => {
      setIsFocused(false);
      onBlurCallback();
    }
  });
};
export default TextFieldEditor;